<template>
  <div class="product-card product-card-coupon">
    <div class="coupon-card-header">{{ t('coupon') }}</div>
    <div class="coupon-inner">
      <button
        class="coupon-dismiss"
        type="button"
        :aria-label="t('moreOptions')"
        @click="openMoreInfoModal(couponFilter)"
      >
        <font-awesome-icon icon="ellipsis-vertical" aria-hidden="true" />
      </button>
      <div class="product-image">
        <WCImage
          :imageSrc="coupon.imageUrl || ''"
          :imageAlt="coupon.name"
          :defaultTitle="coupon.name"
        />
      </div>
      <div class="product-body">
        <router-link
          :to="`/b?g=BRD-${coupon.brand}`"
          :aria-label="`Brand ${coupon.brand}`"
          class="product-brand link-dark underline-link-from-center"
        >
          {{ coupon.brand }}
        </router-link>
        <div v-if="savingsText || couponTitle" class="coupon-savings">
          {{ savingsText || couponTitle }}
        </div>
        <div v-if="expiryText" class="coupon-expiry">{{ t('exp') }}: {{ expiryText }}</div>
      </div>
      <WCCouponClipButtonModern
        :couponId="coupon.id"
        :availableLabel="t('clip')"
        :clippedLabel="t('clippedCoupon')"
        :redeemedLabel="t('couponRedeemed')"
        :couponFilter="couponFilter"
        :disabled="false"
        :isClipped="evaluateClipStatus"
        :isRedeemed="evaluateRedeemedStatus"
        :handleClip="handleClip"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, parseISO } from 'date-fns';
import WCImage from '@/components/WCImage/WCImage.vue';
import WCCouponClipButtonModern from '@/components/WCCouponClipButtonModern/WCCouponClipButtonModern.vue';
import useCoupon from '@/modules/coupons/composables/useCoupon';
import ExternalCoupon from '@/models/entities/items/external-coupon';

interface Props {
  coupon: ExternalCoupon;
  couponFilter?: string;
  savingsText?: string;
}

const props = defineProps<Props>();

const { t } = useI18n();
const {
  handleClip,
  evaluateClipStatus,
  evaluateRedeemedStatus,
  openMoreInfoModal,
  couponTitle,
} = useCoupon(props.coupon);

const expiryText = computed(() => {
  if (!props.coupon.endDate) {
    return null;
  }
  const isoDate = parseISO(props.coupon.endDate);
  return isoDate instanceof Error ? null : format(isoDate, 'M/d/yy');
});
</script>

<style scoped lang="scss">
@import '~@/assets/styles/tools';
@import '~@/assets/styles/settings';

.product-card-coupon {
  display: flex;
  flex-direction: column;
  background: var(--primary-lighter-5);
  border: convert-px-to-rem(0.5) solid #cccccc;
  border-radius: 0.5rem;
  overflow: hidden;
  padding: 0;
}

.coupon-card-header {
  height: 1.875rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--primary-lighter-5);
  color: var(--white, $white);
  font-size: $font-size-sm;
  font-weight: 400;
  border-radius: 0.5rem 0.5rem 0 0;
}

.coupon-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  background: var(--white, $white);
  border: convert-px-to-rem(0.5) solid var(--dark-lighter-5);
  border-radius: 0.5rem;
}

.coupon-dismiss {
  position: absolute;
  top: 0.5625rem;
  right: 0.5625rem;
  z-index: 1;
  width: 1.4375rem;
  height: 1.4375rem;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--white, $white);
  background: var(--dark-lighter-5);
  border: none;
  border-radius: 50%;
  font-size: $font-size-xs;
  cursor: pointer;
}

.product-image {
  width: convert-px-to-rem(100);
  height: convert-px-to-rem(100);
  margin: convert-px-to-rem(16) auto convert-px-to-rem(14);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;

  :deep(img) {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
}

.product-body {
  flex: 1;
  padding: convert-px-to-rem(6) convert-px-to-rem(8) convert-px-to-rem(8);
  background: transparent;
}

.product-brand {
  display: inline-block;
  max-width: 100%;
  font-size: $font-size-sm;
  font-weight: 400;
  line-height: convert-px-to-rem(16);
  margin-bottom: convert-px-to-rem(4);
  color: var(--dark, $dark);
}

.coupon-savings {
  font-size: convert-px-to-rem(20);
  font-weight: var(--weight-bold, 700);
  color: var(--dark, $dark);
  margin-bottom: convert-px-to-rem(2);
}

.coupon-expiry {
  font-size: $font-size-xs;
  font-weight: 400;
  color: var(--dark, $dark);
}
</style>
